/**
 * layers (HOF-467) — the Deckungsbeitrag drawer's cost lines, grouped for the design-system
 * ``MarginBreakdown`` organism.
 *
 * The fact carries costs by ACCOUNT (``direct_costs.saatgut`` … ``allocated_overhead``); the
 * drawer reads them by cost BEHAVIOUR (the WF model's Schichten — ertragsabhängig je kg,
 * ertragsunabhängig je ha, Investition, Absatz) or, for an arable cycle, in the KTBL order
 * advisors compare against (Direktkosten → DkfL → Arbeitserledigung → DB1). Both views
 * REGROUP the same lines; neither re-derives a number: every subtotal the organism prints is
 * the materialiser's own ``dkfl`` / ``db1`` / ``db2`` handed in as ``subtotal.value``, and
 * ``layers.test.ts`` pins that the mapped lines add up to it on a consistent fact — a bucket
 * dropped or double-counted here fails that test instead of reaching a farmer.
 *
 * Phase 1 (HOF-467): the accounts the fact carries map onto layers B / C / D; layer A
 * (Pflücklohn, Verpackung, Kühlung), Landkosten, AfA and Absatzkosten were not lines on the
 * fact and rendered as PENDING rows — a dash, a ``noch nicht erfasst`` flag whose hover says
 * why, and the same sentence in the Rechenweg panel. Never 0,00 €, never omitted silently.
 *
 * #4172 wave 2/3 fills layer A in. The five labour buckets (``pflueck``, ``nebenkosten``,
 * ``verpackung``, ``kuehlung``, ``pflanzung``) become REAL lines on a fact that books them
 * and keep their pending disclosure on a fact that does not — the HOF-518 Landkosten
 * pattern, applied to five lines at once, and for the identical reason: a tenant whose
 * payroll never reaches a cycle must read „noch nicht erfasst", never 0,00 €. 0,00 € says nobody
 * was paid; the pending row says nobody wrote it down, and those are different facts about
 * the farm.
 *
 * 2026-10-07 — two of those five no longer print on their own: Pflück-Nebenkosten reads inside
 * Pflücklohn and Pflanzarbeit inside Löhne (``sourceBreakdown.lineBuckets``). The fact keeps
 * all five buckets; the drawer reads them as three lines plus Löhne.
 */
import type {
  MarginBreakdownBlock,
  MarginBreakdownFlag,
  MarginBreakdownRow,
  MarginBreakdownViewOption,
} from '@hofsync/design-system';
import {
  DUENGER_PSM_LINE,
  establishmentWindowLabel,
  establishmentWindowText,
  hasCountedMaterial,
  lineMagnitude,
} from './sourceBreakdown';
import type { DeckungsbeitragRow } from './types';

export type LayerView = 'schichten' | 'ktbl';

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/** The drawer's regrouping switcher — offered for arable cycles only (``isArableCrop``). */
export const LAYER_VIEW_OPTIONS: MarginBreakdownViewOption[] = [
  { id: 'schichten', label: 'Schichten' },
  { id: 'ktbl', label: 'KTBL' },
];

export function isLayerView(v: unknown): v is LayerView {
  return v === 'schichten' || v === 'ktbl';
}

/**
 * HOF-299 — the merged Dünger/PSM line (Antonio, 2026-09-04). One label, one €, one
 * breakdown; the hover spells the abbreviation out for a reader who has never met „PSM".
 */
/**
 * HOF-518 — the Landkosten line's key and label. The key matches the fact's own bucket
 * name, so `?line=land` addresses it and `LINE_BASIS` is keyed by exactly what the organism
 * selects; the label matches the PENDING row it replaces, so a farmer watching the line
 * become real sees the same words gain a number.
 */
export const LAND_LINE = 'land';
export const LAND_LABEL = 'Landkosten';
/** HOF-518 — the curated-rate label. A contract-derived rate keeps :data:`LAND_LABEL`. */
export const LAND_ESTIMATE_LABEL = 'Landkosten (geschätzt)';

/**
 * The Sonstige hover when the line carries an estimated Substrat charge (2026-10-06). The
 * LABEL stays — most of the line is booked money, and „(geschätzt)" on all of it would be the
 * over-labelling twin of the under-labelling HOF-518 guards against — but the cascade is on
 * screen whether or not the panel is opened, so the guessed part is named here too.
 */
export const SONSTIGE_SUBSTRAT_ESTIMATE_HOVER =
  'Enthält geschätztes Substrat (Laufmeter × Liter je Laufmeter × Preis je m³) — einzeln im Rechenweg.';

/**
 * Layer C „Investition" (2026-10-06) — AfA, Zinsen, Reparatur und Versicherung per
 * installation class. The key is the fact's own bucket name (``?line=investition``, the
 * ``LINE_BASIS`` key); the label keeps the four words of the pending row it replaces, so a
 * farmer watching the slot become real sees the same words gain a number.
 */
export const INVESTITION_LINE = 'investition';
export const INVESTITION_LABEL = 'Investition (AfA, Zinsen, Reparatur, Versicherung)';
export const INVESTITION_HOVER =
  'Einmalige Anschaffungen (Tunnel, Stellage, Bewässerung …), auf ihre Nutzungsdauer verteilt — je Klasse im Rechenweg.';
/**
 * The label stays — „(geschätzt)" inside a label that already carries a parenthesis reads as
 * noise — so the estimate rides in the hover, on the Sonstige/Substrat precedent, and on
 * every class in the panel as the „geschätzt" badge.
 */
export const INVESTITION_ESTIMATE_HOVER = `${INVESTITION_HOVER} Enthält geschätzte Werte: Richtwerte oder nach Fläche verteilte Betriebssummen.`;

export const DUENGER_PSM_LABEL = 'Dünger/PSM';
export const DUENGER_PSM_HOVER = 'Düngemittel und Pflanzenschutzmittel — beide Kostenarten in einer Zeile.';

/**
 * #4172 wave 2 — the five labour buckets, in the order the Schichten view reads them.
 *
 * They are ordinary ``direct_costs`` keys, so ``lineMagnitude`` / ``lineBasis`` / the
 * ``?line=`` deep link all reach them with no special case. What is NOT ordinary is the
 * gate: each renders as a real line only where the fact books money, and keeps its
 * ``PENDING_LINES`` disclosure otherwise (see the module docstring).
 */
export const PFLUECK_LINE = 'pflueck';
export const VERPACKUNG_LINE = 'verpackung';
export const KUEHLUNG_LINE = 'kuehlung';

export const PFLUECK_LABEL = 'Pflücklohn';
export const PFLUECK_HOVER =
  'Der gemessene Erntelohn dieses Anbaus — aus der Lohnabrechnung, nicht geschätzt und nicht auf die Kulturen verteilt.';
export const VERPACKUNG_LABEL = 'Verpackung';
export const VERPACKUNG_HOVER =
  'Anteil dieses Anbaus an der monatlichen Packhallen-Lohnsumme, verteilt nach gepflückten Kilogramm.';
export const KUEHLUNG_LABEL = 'Kühlung, Pfandkisten';
/**
 * The „geschätzt" suffix, on the Leistung/Landkosten precedent and for their reason: the
 * cascade is on screen whether or not the Rechenweg panel is opened, so the one claim a
 * reader must not miss belongs on the line itself. Kühlung is the one labour line whose €
 * is a REFERENCE RATE × kg rather than a measurement, and saying so is the whole point.
 */
export const KUEHLUNG_ESTIMATE_LABEL = 'Kühlung, Pfandkisten (geschätzt)';
export const KUEHLUNG_HOVER = 'Gepflückte Kilogramm × Kühl-/Pfandkisten-Satz je Kilogramm.';
/**
 * 2026-10-07 — Löhne is every wage hour that is not a harvest hour: Pflegearbeit,
 * Pflanzarbeit and the work no single cycle was stamped on. Pflanzarbeit had its own line
 * (#4172); it now reads inside Löhne, item by item in the Rechenweg.
 */
export const LOEHNE_LABEL = 'Löhne';
export const LOEHNE_HOVER =
  'Pflegearbeit, Pflanzarbeit und Sonstige Arbeit; mit Lohnabrechnung auch Lohnnebenkosten (Arbeitgeber) ' +
  'oder Arbeitgeberkosten laut Lohnabrechnung, Umlage Arbeit ohne Feld und Fester Kulturanteil — je Monat ' +
  'im Rechenweg.';

/** The Schichten view's layer-A subtotal — what is left of the Leistung once the Ernte bezahlt ist. */
export const LEISTUNG_NACH_PFLUECKEN_LABEL = 'Leistung nach Pflücken';

/**
 * The three lines layer A holds — the yield-dependent handling ``leistung_nach_pfluecken`` nets
 * off. Pflück-Nebenkosten is no longer one of them: it reads inside Pflücklohn (2026-10-07).
 */
export const LAYER_A_LINES: readonly string[] = [PFLUECK_LINE, VERPACKUNG_LINE, KUEHLUNG_LINE];

/** One cost line the fact carries, in the KTBL cascade order the drawer has always used. */
export interface CostLine {
  /** The cascade-line key — ``?line=`` value, ``sourceBreakdown`` bucket, organism row id. */
  key: string;
  label: string;
  /** Euro magnitude (≥ 0); the organism row carries it negated. */
  magnitude: number;
  /** #2608 — the hover explaining this line's €, where the fact carries the explanation. */
  title?: string;
}

/**
 * #4172 — the four yield-dependent labour lines, each emitted only where the fact books
 * money for it.
 *
 * The gate is the bucket's own €, not ``labour_observed``, and the two are deliberately
 * different questions. ``labour_observed`` says whether the payroll reached this cycle AT
 * ALL — it is what decides whether the layer-A SUBTOTAL may print (below). A single
 * bucket's 0 is a narrower claim: this cycle recorded no packing money. Rendering that as
 * „0,00 € Verpackung" would tell a farmer their packing hall was free; the pending row
 * says it was not recorded, which is what actually happened.
 */
function labourLines(row: DeckungsbeitragRow): CostLine[] {
  const c = row.direct_costs;
  const lines: CostLine[] = [];
  // 2026-10-07 — Pflücklohn spans ``pflueck`` + ``nebenkosten`` (``lineBuckets``); the
  // Nebenkosten no longer print on a line of their own.
  const pflueck = lineMagnitude(row, PFLUECK_LINE);
  if (pflueck > 0) lines.push({ key: PFLUECK_LINE, label: PFLUECK_LABEL, magnitude: pflueck, title: PFLUECK_HOVER });
  // codex r1 P2 — counted packaging material is evidence even when no invoice priced a
  // single piece: the line is then a real 0,00 € lower bound whose rows name the articles,
  // not „nicht erfasst". The pending row stays for a fact that counted nothing.
  if (c.verpackung > 0 || hasCountedMaterial(row)) {
    lines.push({ key: VERPACKUNG_LINE, label: VERPACKUNG_LABEL, magnitude: c.verpackung, title: VERPACKUNG_HOVER });
  }
  if (c.kuehlung > 0) {
    lines.push({
      key: KUEHLUNG_LINE,
      // The badge follows whether the RATE is a reference or the farm's own confirmation —
      // the HOF-518 grade argument. A farm-confirmed Kühlsatz is not a guess.
      label: row.kuehlung_estimated ? KUEHLUNG_ESTIMATE_LABEL : KUEHLUNG_LABEL,
      magnitude: c.kuehlung,
      title: KUEHLUNG_HOVER,
    });
  }
  return lines;
}

/**
 * The cost lines of one fact: the direct-cost buckets (Dünger and PSM merged, HOF-299),
 * plus Etablierung, Gemeinkosten, Landkosten and the #4172 labour lines when the fact books
 * them — the lines the pre-HOF-467 cascade rendered, minus the subtotals, which the organism
 * now prints from ``subtotal.value``.
 */
export function costLines(row: DeckungsbeitragRow): CostLine[] {
  const c = row.direct_costs;
  const lines: CostLine[] = [
    // ── layer A, the yield-dependent handling (#4172 wave 2) ───────────────────
    // Emitted FIRST so ``costLines`` reads in the order the Schichten view prints, and
    // gated on money for the HOF-518 reason: a 0 keeps the PENDING disclosure.
    ...labourLines(row),
    { key: 'saatgut', label: 'Saatgut/Pflanzen', magnitude: c.saatgut },
    // HOF-299 — the fact keeps its two buckets; the line's € is their cent-exact Σ, read
    // through the same helper the breakdown reconciles against so the two cannot disagree.
    { key: DUENGER_PSM_LINE, label: DUENGER_PSM_LABEL, magnitude: lineMagnitude(row, DUENGER_PSM_LINE), title: DUENGER_PSM_HOVER },
    {
      key: 'sonstige',
      label: 'Sonstige Direktkosten',
      magnitude: c.sonstige,
      ...(row.substrat_estimated ? { title: SONSTIGE_SUBSTRAT_ESTIMATE_HOVER } : {}),
    },
  ];
  if (row.establishment_amortised > 0) {
    // #2608 — "(anteilig)" begged the question this line could not answer: a share of WHAT?
    // The R5 window rides on the fact, so the label names it and the hover spells it out; both
    // degrade to the bare label on a fact without a window. The qualifier is REPLACED, never
    // suffixed, on the ``expensed_whole`` shape — "(anteilig) · voll in diesem Jahr" would say
    // pro-rata and not-pro-rata in one breath.
    const windowLabel = establishmentWindowLabel(row.establishment_detail);
    const windowText = establishmentWindowText(row.establishment_detail);
    const base = row.establishment_detail?.basis === 'expensed_whole' ? 'Etablierung' : 'Etablierung (anteilig)';
    lines.push({
      key: 'etablierung',
      label: windowLabel ? `${base} · ${windowLabel}` : base,
      magnitude: row.establishment_amortised,
      ...(windowText ? { title: windowText } : {}),
    });
  }
  // Layer C — Investition, beside Etablierung, inside ``dkfl`` exactly like it. A line when the
  // fact carries the block (even at 0 € — every class refused, and the reasons are the
  // content) or books the bucket; otherwise the slot keeps its PENDING row, for the HOF-518
  // reason: a 0,00 € here would say the farm's installations cost nothing.
  if (row.investition !== null || c.investition > 0) {
    lines.push({
      key: INVESTITION_LINE,
      label: INVESTITION_LABEL,
      magnitude: c.investition,
      title: row.investition_estimated ? INVESTITION_ESTIMATE_HOVER : INVESTITION_HOVER,
    });
  }
  if (row.allocated_overhead > 0) {
    lines.push({ key: 'gemeinkosten', label: 'Gemeinkosten (anteilig)', magnitude: row.allocated_overhead });
  }
  // 2026-10-07 — Löhne spans ``loehne`` + ``pflanzung`` (``lineBuckets``).
  lines.push({ key: 'loehne', label: LOEHNE_LABEL, magnitude: lineMagnitude(row, 'loehne'), title: LOEHNE_HOVER });
  lines.push({ key: 'maschinen', label: 'Maschinen', magnitude: c.maschinen });
  // HOF-518 — Landkosten, conditional like Etablierung and Gemeinkosten above and for the
  // same reason, sharpened by what this particular line used to be. Until now it was a
  // PENDING row on every fact ("Landkosten (Pacht) werden noch nicht je Schlag erfasst"),
  // and the honest reading of a 0 is still exactly that: a tenant whose curated books have
  // not been published, or a fact materialised before this line existed. Emitting a 0,00 €
  // line for those would replace a disclosure that says „not recorded yet" with a number
  // that says „your land is free" — the one thing this slice exists to stop.
  if (c.land > 0) {
    // HOF-518 — the „geschätzt" suffix on the Leistung line's precedent, and for its reason:
    // the cascade is on screen whether or not the panel below is opened, so the one claim a
    // reader must not miss belongs here rather than only in the Rechenweg. Tied to the price
    // GRADE — a rate derived from the farm's own Pachtvertrag is not a guess, even though it
    // is still spread flat over every hectare.
    lines.push({
      key: LAND_LINE,
      label: row.land_estimated ? LAND_ESTIMATE_LABEL : LAND_LABEL,
      magnitude: c.land,
    });
  }
  return lines;
}

/** Σ of every cost line — what ``leistung − Σ`` must equal on a consistent fact (``db1``). */
export function costLinesTotal(row: DeckungsbeitragRow): number {
  return costLines(row).reduce((sum, line) => sum + line.magnitude, 0);
}

/** A layer category the fact does not carry yet — rendered pending, never as a number. */
export interface PendingLine {
  /** Organism row id (``pending:`` prefixed, so it can never collide with a cascade key). */
  id: string;
  label: string;
  /** Why there is no figure — the flag's hover and the Rechenweg panel's text. */
  text: string;
}

export const PENDING_ROW_PREFIX = 'pending:';

const pending = (slug: string, label: string, text: string): PendingLine => ({
  id: `${PENDING_ROW_PREFIX}${slug}`,
  label,
  text,
});

/** The row flag every pending line carries; its hover is the line's own sentence. */
export const PENDING_FLAG_LABEL = 'noch nicht erfasst';

export const PENDING_LINES = {
  // #4172 — the sentence had to change with the engine. It used to say „der Pflücklohn
  // steckt heute in der Zeile Löhne", which was true of the per-cycle wage lump wave 1
  // removed: a fact without labour records now carries NO picking wage anywhere, in Löhne
  // or otherwise. Telling a farmer to look for it in a line that does not contain it is
  // worse than saying nothing, because they would go and check.
  pflueck: pending(
    'pflueck',
    PFLUECK_LABEL,
    'Für diesen Anbau ist keine Lohnabrechnung erfasst — der Pflücklohn kann deshalb nicht ausgewiesen werden und steckt auch in keiner anderen Zeile.',
  ),
  verpackung: pending('verpackung', VERPACKUNG_LABEL, 'Verpackungskosten werden für diesen Anbau noch nicht erfasst.'),
  kuehlung: pending('kuehlung', KUEHLUNG_LABEL, 'Kühlung und Pfandkisten werden für diesen Anbau noch nicht erfasst.'),
  land: pending('land', 'Landkosten', 'Landkosten (Pacht) werden noch nicht je Schlag erfasst.'),
  afa: pending(
    'afa',
    'AfA, Zinsen, Reparatur, Versicherung',
    'Abschreibung, Zinsen, Reparatur und Versicherung werden noch nicht je Kultur erfasst.',
  ),
  absatz: pending(
    'absatz',
    'Absatzkosten',
    'Absatzkosten (Auslieferung, Provision, Gebühren) werden noch nicht je Deal erfasst.',
  ),
} as const;

/** The pending line behind an organism row id, or null for a cascade key. */
export function pendingLine(rowId: string | null): PendingLine | null {
  if (!rowId || !rowId.startsWith(PENDING_ROW_PREFIX)) return null;
  return Object.values(PENDING_LINES).find((line) => line.id === rowId) ?? null;
}

const pendingRow = (line: PendingLine): MarginBreakdownRow => ({
  id: line.id,
  label: line.label,
  value: null,
  flag: { label: PENDING_FLAG_LABEL, tone: 'neutral', title: line.text },
});

/** What the drawer adds to a cost line: the #2476 reason flag and whether it opens a Rechenweg. */
export interface LayerDecoration {
  flag?: MarginBreakdownFlag;
  disabled?: boolean;
  /** Overrides the line's own hover (a disabled line explains why it is inert). */
  title?: string;
}

export interface LayerBlocksOptions {
  decorate: (line: CostLine) => LayerDecoration;
  /** Whole-row honesty flags, shown on the DB1 subtotal (the row-quality chips, #2773). */
  db1Flags?: MarginBreakdownFlag[];
}

const DB1_LABEL = 'Deckungsbeitrag (DB1)';

/**
 * The organism blocks for one fact and one view.
 *
 * Subtotals carry the PERSISTED figure (``subtotal.value``): DkfL / DB1 / DB2 are printed as
 * the materialiser wrote them, never as a running sum of rounded buckets (each bucket is
 * rounded to the cent before it reaches the fact, so a running sum can drift by cents from
 * the rounded total the worker computed on the unrounded accumulators).
 */
export function layerBlocks(row: DeckungsbeitragRow, view: LayerView, opts: LayerBlocksOptions): MarginBreakdownBlock[] {
  const byKey = new Map(costLines(row).map((line) => [line.key, line]));
  const rowFor = (key: string): MarginBreakdownRow | null => {
    const line = byKey.get(key);
    if (!line) return null;
    const deco = opts.decorate(line);
    return {
      id: line.key,
      label: line.label,
      // normalise a 0 line's ``-0`` so it reads 0,00 €, never −0,00 €
      value: line.magnitude === 0 ? 0 : -line.magnitude,
      ...(deco.flag ? { flag: deco.flag } : {}),
      ...(deco.disabled ? { disabled: true } : {}),
      ...(deco.title ?? line.title ? { title: deco.title ?? line.title } : {}),
    };
  };
  const rows = (...keys: string[]): MarginBreakdownRow[] =>
    keys.map(rowFor).filter((r): r is MarginBreakdownRow => r !== null);
  /**
   * #4172 — a labour slot is EITHER its real line or its pending disclosure, never both and
   * never empty. ``rows()`` drops a key ``costLines`` did not emit, so the two arms cannot
   * collide; this is the HOF-518 land pattern, spelled once for four lines.
   */
  const orPending = (key: string, line: PendingLine): MarginBreakdownRow[] =>
    byKey.has(key) ? rows(key) : [pendingRow(line)];
  /**
   * #4172 — the layer-A subtotal prints the fact's own ``leistung_nach_pfluecken``, but only
   * where the fact actually books yield-dependent handling. On a cycle that books none it
   * would equal the Leistung exactly, and a „Leistung nach Pflücken = Leistung" line under
   * four „noch nicht erfasst" rows reads as a claim that picking was free — the one thing
   * those four rows exist to deny.
   */
  const layerAPriced = LAYER_A_LINES.some((key) => byKey.has(key));
  const leistungNachPfluecken = layerAPriced
    ? { label: LEISTUNG_NACH_PFLUECKEN_LABEL, value: row.leistung_nach_pfluecken }
    : { label: LEISTUNG_NACH_PFLUECKEN_LABEL, pending: true };
  /**
   * 2026-10-07 — the KTBL DkfL with Pflanzarbeit taken back out. The engine subtracts
   * ``pflanzung`` inside ``dkfl``; the drawer now prints it inside Löhne, under
   * Arbeitserledigung, so the persisted figure would stop being the Σ of the Direktkosten
   * rows above it. Adding the bucket back keeps the printed DkfL that Σ — and is the KTBL
   * reading anyway (planting labour is Arbeitserledigung, not a Direktkost). Verbatim on every
   * fact that books no Pflanzarbeit, which is every arable cycle today.
   */
  const ktblDkfl = row.direct_costs.pflanzung > 0 ? round2(row.dkfl + row.direct_costs.pflanzung) : row.dkfl;
  const db1Subtotal = { label: DB1_LABEL, final: true, value: row.db1, ...(opts.db1Flags?.length ? { flags: opts.db1Flags } : {}) };
  const direktzahlung: MarginBreakdownBlock[] =
    row.direktzahlung > 0
      ? [
          {
            id: 'E',
            title: 'Direktzahlung',
            rows: [{ id: 'dz', label: 'Direktzahlung', value: row.direktzahlung, disabled: true }],
            subtotal: { label: 'DB2', value: row.db2 },
          },
        ]
      : [];

  if (view === 'ktbl') {
    return [
      {
        id: 'direktkosten',
        title: 'Direktkosten',
        // HOF-518 — Landkosten is ertragsunabhängig and above DB1, so it belongs to the
        // DkfL group here exactly as it does to block B in the Schichten view. Putting it
        // in Arbeitserledigung would say a Pacht is a working cost.
        //
        // #4172 — Verpackung and Kühlung join it, and Pflücklohn joins Arbeitserledigung
        // below: the engine's own ``dkfl`` / ``db1`` split, read back. Pflanzarbeit is the one
        // exception since 2026-10-07 — it reads inside Löhne, and ``ktblDkfl`` above adds it
        // back so the printed DkfL is still the Σ of these rows.
        rows: rows(
          'saatgut',
          DUENGER_PSM_LINE,
          'sonstige',
          VERPACKUNG_LINE,
          KUEHLUNG_LINE,
          LAND_LINE,
          'etablierung',
          // Layer C — the engine puts it in ``dkfl``, beside Etablierung.
          INVESTITION_LINE,
          'gemeinkosten',
        ),
        subtotal: { label: 'DkfL', value: ktblDkfl },
      },
      {
        id: 'arbeitserledigung',
        title: 'Arbeitserledigung',
        rows: [...rows(PFLUECK_LINE), ...rows('loehne'), ...rows('maschinen')],
        subtotal: db1Subtotal,
      },
      ...direktzahlung,
    ];
  }
  return [
    {
      id: 'A',
      title: 'Ertragsabhängig',
      // #4172 — three slots, each a real line or its own disclosure. Pflück-Nebenkosten left
      // the layer on 2026-10-07; its € reads inside Pflücklohn.
      rows: [
        ...orPending(PFLUECK_LINE, PENDING_LINES.pflueck),
        ...orPending(VERPACKUNG_LINE, PENDING_LINES.verpackung),
        ...orPending(KUEHLUNG_LINE, PENDING_LINES.kuehlung),
      ],
      subtotal: leistungNachPfluecken,
    },
    {
      id: 'B',
      title: 'Ertragsunabhängig',
      // HOF-518 — the land row is the REAL line where the fact books one and the pending
      // disclosure where it does not. ``rows()`` drops a key ``costLines`` did not emit, so
      // the two can never both appear, and the slot is never empty. Pflanzarbeit has no slot
      // of its own since 2026-10-07: it reads inside Löhne.
      rows: [
        ...rows('saatgut'),
        ...rows(DUENGER_PSM_LINE, 'sonstige'),
        ...rows('loehne'),
        ...rows('maschinen'),
        ...orPending(LAND_LINE, PENDING_LINES.land),
      ],
    },
    // Layer C — the Investition slot is the real line or the pending disclosure, never both.
    { id: 'C', title: 'Investition', rows: [...rows('etablierung'), ...orPending(INVESTITION_LINE, PENDING_LINES.afa)] },
    {
      id: 'D',
      title: 'Absatz',
      rows: [...rows('gemeinkosten'), pendingRow(PENDING_LINES.absatz)],
      subtotal: db1Subtotal,
    },
    ...direktzahlung,
  ];
}
